import axios from 'axios';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { Message, Modal } from '@arco-design/web-vue';
import { useUserStore } from '@/store';
import { getToken, setToken, getRefreshToken, getUserId, clearToken } from '@/utils/auth';
import { refreshToken } from '@/api/user';

export interface HttpResponse<T = unknown> {
  status: number;
  msg: string;
  code: number;
  data: T;
}

// 刷新 token 相关的变量
let isRefreshing = false;
let requests: ((token: string) => void)[] = [];
let isSessionExpiring = false;

function redirectToLoginWithNotice(content = '登录已过期，请重新登录') {
  if (isSessionExpiring) {
    return;
  }
  isSessionExpiring = true;
  Message.warning({
    content,
    duration: 3 * 1000,
  });
  const userStore = useUserStore();
  userStore.logout();
  window.location.href = '/login';
}

function isAuthRefreshRequest(config?: AxiosRequestConfig) {
  return config?.url?.includes('/api/auth/refresh');
}

function isTokenExpiredResponse(res: any) {
  return (
    res?.code === 40102 ||
    res?.message === '登录已过期' ||
    res?.msg === '登录已过期'
  );
}

async function refreshAccessTokenAndRetry(config: AxiosRequestConfig) {
  if (!isRefreshing) {
    isRefreshing = true;
    try {
      const userId = getUserId();
      const refToken = getRefreshToken();

      if (!userId || !refToken) {
        throw new Error('Refresh token info missing');
      }

      const refreshRes = (await refreshToken(userId, 'web', refToken)) as any;

      let newAccessToken = '';
      if (refreshRes.data && refreshRes.data.accessToken) {
        newAccessToken = refreshRes.data.accessToken;
      } else if (refreshRes.accessToken) {
        newAccessToken = refreshRes.accessToken;
      }

      if (!newAccessToken) {
        throw new Error('Refresh token response invalid');
      }

      const isRemembered = !!localStorage.getItem('token');
      setToken(newAccessToken, refToken, isRemembered);

      requests.forEach((cb) => cb(newAccessToken));
      requests = [];

      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${newAccessToken}`;
      return axios(config);
    } catch (e) {
      console.error('Token refresh failed:', e);
      requests.forEach((cb) => cb(''));
      requests = [];
      redirectToLoginWithNotice('登录已过期，请重新登录');
      return Promise.reject(e);
    } finally {
      isRefreshing = false;
    }
  }

  return new Promise((resolve) => {
    requests.push((token: string) => {
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
        resolve(axios(config));
      } else {
        resolve(Promise.reject(new Error('Token refresh failed')));
      }
    });
  });
}

// 取消注释这段代码，使用环境变量中设置的API基础URL
if (import.meta.env.VITE_API_BASE_URL) {
  axios.defaults.baseURL = import.meta.env.VITE_API_BASE_URL;
  console.log(
    'interceptor.ts: 环境变量中设置baseURL =',
    import.meta.env.VITE_API_BASE_URL
  );
} else {
  // 如果环境变量为空，则直接设置为8090端口
  axios.defaults.baseURL = 'http://localhost:8090';
  console.log(
    'interceptor.ts: 环境变量为空，直接设置baseURL = http://localhost:8090'
  );
}

// 注释掉这行代码
// axios.defaults.baseURL = '';
// console.log('interceptor.ts: 设置axios.defaults.baseURL为空，使用相对路径');

axios.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    // 打印完整的请求URL
    const baseURL = config.baseURL || axios.defaults.baseURL || '';
    const url = config.url || '';
    console.log('完整请求URL:', `${baseURL}${url}`);

    // let each request carry token
    // this example using the JWT token
    // Authorization is a custom headers key
    // please modify it according to the actual situation
    const token = getToken();
    if (token) {
      if (!config.headers) {
        config.headers = {};
      }
      // 如果 header 中没有 Authorization，则使用 access token
      // 这样可以允许 refresh 接口手动传递 refresh token
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    // do something
    return Promise.reject(error);
  }
);
// add response interceptors
axios.interceptors.response.use(
  async (response: AxiosResponse<HttpResponse>) => {
    // 打印完整响应，便于调试
    console.log('响应数据:', response.data);

    const res = response.data;
    const silentError = Boolean((response.config as any)?.silentError);

    // 如果是登录接口，直接返回响应数据
    if (
      response.config.url?.includes('/api/user/login') ||
      response.config.url?.includes('/api/auth/login')
    ) {
      return response;
    }

    // 检查响应格式，支持多种code值
    // 成功的code可能是20000或200
    if (res.code && res.code !== 20000 && res.code !== 200) {
      // 检查是否需要刷新 Token (40102 或 登录已过期)
      // 注意：如果是 refresh 接口本身报错，则不进行重试，直接登出
      if (
        isTokenExpiredResponse(res) &&
        !isAuthRefreshRequest(response.config)
      ) {
        return refreshAccessTokenAndRetry(response.config);
      }

      if ([40302, 40303].includes(res.code)) {
        clearToken();
        redirectToLoginWithNotice(res.msg || (res as any).message || '账号状态异常，请重新登录');
        return Promise.reject(new Error(res.msg || (res as any).message || '账号状态异常'));
      }

      const resAny = res as any;
      const text =
        resAny?.data?.message ??
        resAny?.data?.msg ??
        resAny?.message ??
        resAny?.msg ??
        'Error';
      if (!silentError) {
        Message.error({
          content: text,
          duration: 5 * 1000,
        });
      }

      // 处理401未授权的情况
      if (res.code === 401) {
        // 仅在非登录请求中处理
        if (
          !response.config.url?.includes('/api/user/info') &&
          !response.config.url?.includes('/api/user/login') &&
          !response.config.url?.includes('/api/auth/login')
        ) {
          redirectToLoginWithNotice('未登录或登录已过期，请重新登录');
        }
        return Promise.reject(new Error('未登录或登录已过期'));
      }

      // 50008: Illegal token; 50012: Other clients logged in; 50014: Token expired;
      if (
        [50008, 50012, 50014].includes(res.code) &&
        response.config.url !== '/api/user/info'
      ) {
        Modal.error({
          title: 'Confirm logout',
          content:
            'You have been logged out, you can cancel to stay on this page, or log in again',
          okText: 'Re-Login',
          async onOk() {
            const userStore = useUserStore();
            await userStore.logout();
            window.location.reload();
          },
        });
      }
      return Promise.reject(new Error(res.msg || 'Error'));
    }
    return res;
  },
  (error) => {
    // 打印错误信息，便于调试
    console.error('响应错误详情:', error);
    const silentError = Boolean((error.config as any)?.silentError);

    if (
      error.response &&
      isTokenExpiredResponse(error.response.data) &&
      !isAuthRefreshRequest(error.config)
    ) {
      return refreshAccessTokenAndRetry(error.config);
    }

    // 处理401错误响应
    if (error.response && error.response.status === 401) {
      // 仅在非登录请求中处理
      if (
        !error.config.url?.includes('/api/user/info') &&
        !error.config.url?.includes('/api/user/login') &&
        !error.config.url?.includes('/api/auth/login')
      ) {
        redirectToLoginWithNotice('未登录或登录已过期，请重新登录');
      }
      return Promise.reject(new Error('未登录或登录已过期'));
    }

    if (!silentError) {
      Message.error({
        content: error.message || 'Request Error',
        duration: 5 * 1000,
      });
    }
    return Promise.reject(error);
  }
);
